import { ApplicationWithId } from "../interfaces/IApplications";
import { useEffect, useState } from "react";
import { useLoggingActions } from "./useLogging";
import {
  findApplication,
  findUserApplications,
  findApplicationsSnapshot,
  createApplication,
  deleteApplication,
  updateApplication as updateApplicationInFirestore,
} from "../utils/firestore";

// Interfejs dla wyniku operacji
interface OperationResult {
  success: boolean;
  error?: string;
  application?: ApplicationWithId;
}

export const useApplications = (userId: string) => {
  const [applications, setApplications] = useState<ApplicationWithId[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const {
    logApplicationCreate,
    logApplicationUpdate,
    logApplicationDelete,
    logApplicationStatusChange,
  } = useLoggingActions();

  useEffect(() => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    // STARA IMPLEMENTACJA - wykomentowana dla łatwego przywrócenia
    // const fetchData = async () => {
    //   setIsLoading(true);
    //   try {
    //     // Pobieramy wszystkie aplikacje użytkownika
    //     const data = await findUserApplications(userId as string);
    //     setApplications(data);
    //   } catch (error) {
    //     console.error("Błąd podczas pobierania aplikacji:", error);
    //   } finally {
    //     setIsLoading(false);
    //   }
    // };
    // fetchData();

    // NOWA IMPLEMENTACJA - onSnapshot
    setIsLoading(true);
    const unsubscribe = findApplicationsSnapshot(userId, (applicationsData) => {
      setApplications(applicationsData as ApplicationWithId[]);
      setIsLoading(false);
    });

    // Cleanup - odsubskrybowanie przy unmount lub zmianie userId
    return () => {
      unsubscribe();
    };
  }, [userId]);

  // Orkiestracja procesów - dodawanie zgłoszenia
  const handleApplicationAdd = async (
    applicationData: ApplicationWithId
  ): Promise<OperationResult> => {
    try {
      // 1. Zapis do Firestore
      const createdApplication = await createApplication(applicationData, applicationData.userId);

      // 2. Logowanie
      try {
        await logApplicationCreate(createdApplication, true, undefined, {
          contestId: createdApplication.contestId,
          contestName: createdApplication.contestName,
        });
      } catch (loggingError) {
        console.warn("Błąd logowania (ignorowany):", loggingError);
      }

      // 3. Aktualizacja stanu
      setApplications((prev) => [...prev, createdApplication]);

      // 4. Zwróć wynik
      return { success: true, application: createdApplication };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      try {
        await logApplicationCreate(applicationData, false, errorMessage, {
          contestId: applicationData.contestId,
          contestName: applicationData.contestName,
        });
      } catch (loggingError) {
        console.warn(
          "Błąd logowania błędu dot. tworzenia zgłoszenia (useApplications_82):",
          loggingError
        );
      }
      return { success: false, error: errorMessage };
    }
  };

  // Orkiestracja procesów - aktualizacja zgłoszenia
  const handleApplicationUpdate = async (
    applicationId: string,
    updatedData: Partial<ApplicationWithId>
  ): Promise<OperationResult> => {
    try {
      // 1. Znajdź stary stan
      const oldApplication = applications.find((app) => app.id === applicationId);
      if (!oldApplication) {
        return { success: false, error: "Zgłoszenie nie zostało znalezione" };
      }

      // 2. Przygotuj nowy stan
      const newApplication = { ...oldApplication, ...updatedData };

      // 3. Sprawdź czy zmienił się status
      const statusChanged = oldApplication.status !== newApplication.status;

      // 4. Aktualizuj w Firestore
      await updateApplicationInFirestore(newApplication);

      // 5. Aktualizuj stan lokalny
      setApplications((prev) =>
        prev.map((app) => (app.id === applicationId ? newApplication : app))
      );

      // 6. Logowanie
      try {
        if (statusChanged) {
          // Logowanie zmiany statusu
          await logApplicationStatusChange(
            newApplication,
            oldApplication.status,
            newApplication.status,
            true,
            undefined,
            {
              contestId: newApplication.contestId,
              contestName: newApplication.contestName,
            }
          );
        } else {
          // Logowanie zwykłej aktualizacji
          await logApplicationUpdate(oldApplication, newApplication, true, undefined, {
            contestId: newApplication.contestId,
            contestName: newApplication.contestName,
          });
        }
      } catch (loggingError) {
        console.warn("Błąd logowania (ignorowany):", loggingError);
      }

      return { success: true, application: newApplication };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      const oldApplication = applications.find((app) => app.id === applicationId);
      if (oldApplication) {
        try {
          await logApplicationUpdate(
            oldApplication,
            { ...oldApplication, ...updatedData },
            false,
            errorMessage,
            {
              contestId: oldApplication.contestId,
              contestName: oldApplication.contestName,
            }
          );
        } catch (loggingError) {
          console.warn(
            "Błąd logowania błędu dot. aktualizacji zgłoszenia (useApplications_161):",
            loggingError
          );
        }
      }
      return { success: false, error: errorMessage };
    }
  };

  // Orkiestracja procesów - usunięcie zgłoszenia
  const handleApplicationDelete = async (applicationId: string): Promise<OperationResult> => {
    try {
      // 1. Znajdź zgłoszenie
      const applicationToDelete = applications.find((app) => app.id === applicationId);
      if (!applicationToDelete) {
        return { success: false, error: "Zgłoszenie nie zostało znalezione" };
      }

      // 2. Usuń z Firestore
      await deleteApplication(applicationId, userId);

      // 3. Aktualizuj stan lokalny
      setApplications((prev) => prev.filter((app) => app.id !== applicationId));

      // 4. Logowanie
      try {
        await logApplicationDelete(applicationToDelete, true, undefined, {
          contestId: applicationToDelete.contestId,
          contestName: applicationToDelete.contestName,
        });
      } catch (loggingError) {
        console.warn(
          "Błąd logowania błędu dot. usunięcia zgłoszenia (useApplications_200):",
          loggingError
        );
      }

      return { success: true };
    } catch (error) {
      // Logowanie błędu
      const errorMessage = error instanceof Error ? error.message : String(error);
      const applicationToDelete = applications.find((app) => app.id === applicationId);
      if (applicationToDelete) {
        try {
          await logApplicationDelete(applicationToDelete, false, errorMessage, {
            contestId: applicationToDelete.contestId,
            contestName: applicationToDelete.contestName,
          });
        } catch (loggingError) {
          console.warn(
            "Błąd logowania błędu dot. usunięcia zgłoszenia (useApplications_205):",
            loggingError
          );
        }
      }
      return { success: false, error: errorMessage };
    }
  };

  const refreshApplication = async (userId: string, applicationId: string) => {
    await findApplication(userId, applicationId).then((refreshedApplication) => {
      setApplications((prev) =>
        prev.map((app) => (app.id === refreshedApplication.id ? refreshedApplication : app))
      );
    });
  };

  return {
    // Stan
    applications,
    isLoading,

    // Orkiestrowane metody
    handleApplicationAdd,
    handleApplicationUpdate,
    handleApplicationDelete,

    // Metody pomocnicze (zachowane dla kompatybilności)
    refreshApplication,
  };
};
